export const FILE_TYPE_CATEGORIES = {
  pdf: { label: "PDF Documents", types: ["application/pdf"] },
  images: {
    label: "Images",
    types: ["image/jpeg", "image/png", "image/gif", "image/bmp", "image/svg+xml", "image/webp"],
  },
  documents: {
    label: "Text Documents",
    types: [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ],
  },
  spreadsheets: {
    label: "Spreadsheets",
    types: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
    ],
  },
  presentations: {
    label: "Presentations",
    types: [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
  },
  emails: {
    label: "Email Files",
    types: ["application/vnd.ms-outlook"],
  },
  bim: {
    label: "BIM Files",
    types: ["application/x-step"],
  },
  archives: {
    label: "Archives",
    types: ["application/zip", "application/x-rar-compressed", "application/x-7z-compressed"],
  },
} as const;

// Container file extensions for special processing
export const CONTAINER_EXTENSIONS = ["zip", "asice", "bdoc", "ddoc"] as const;

// Digitally-signed containers (ASiC-E family — the Estonian/EU e-signature
// formats). A strict subset of CONTAINER_EXTENSIONS: a plain `.zip` is an
// archive, not a sealed envelope, so it keeps the folder-like container
// behaviour and never gets a signed indicator.
export const SIGNED_CONTAINER_EXTENSIONS = ["asice", "bdoc", "ddoc"] as const;

// DWG file extensions for AutoCAD files
export const DWG_EXTENSIONS = ["dwg"] as const;

// IFC (Industry Foundation Classes) extensions for BIM files. Routes uploads
// to the IFC_CONTAINER → IFC_PARSING → IFC_PROCESSED pipeline.
export const IFC_EXTENSIONS = ["ifc"] as const;

// MIME types that identify a zip/container upload (some browsers/OSes report the
// non-standard x-zip-compressed). Estonian e-signature containers
// (.asice/.bdoc/.ddoc) are zips too but are matched by extension.
export const ZIP_CONTENT_TYPES = ["application/zip", "application/x-zip-compressed"] as const;

/**
 * Lower-cased file extension without the leading dot, or "" when there is none.
 * e.g. "Plan_AR.IFC" → "ifc", "README" → "".
 */
export function getFileExtension(filename: string | null | undefined): string {
  if (!filename) return "";
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf(".");
  return dot < 0 || dot === lower.length - 1 ? "" : lower.slice(dot + 1);
}

/** True when the filename is an IFC (BIM) model. Single source for IFC
 * detection — replaces scattered `.endsWith(".ifc")` checks (WHA-3689). */
export function isIfcFile(filename: string | null | undefined): boolean {
  return (IFC_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));
}

/** True when the filename is an AutoCAD DWG drawing. */
export function isDwgFile(filename: string | null | undefined): boolean {
  return (DWG_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));
}

/**
 * True when the filename is a container archive — a plain `.zip` or one of the
 * signed ASiC-E formats. Single source for the previously-duplicated
 * `endsWith(".zip") || endsWith(".asice") || ...` chains in the row renderers.
 */
export function isContainerFile(filename: string | null | undefined): boolean {
  return (CONTAINER_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));
}

/**
 * True when the filename is a digitally-signed container (.asice/.bdoc/.ddoc).
 * Single source for the "this is a sealed, signed envelope" decision used by
 * the folder sidebar, the document rows and the signature panel — a plain
 * `.zip` is a container but NOT signed, so it returns false.
 */
export function isSignedContainerFile(filename: string | null | undefined): boolean {
  return (SIGNED_CONTAINER_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));
}

/**
 * Resolve the upload `container_type` ("dwg" | "ifc" | "zip") that routes a file
 * into its specialised pipeline. DWG/IFC win by extension; zip is detected by a
 * container extension OR a zip MIME type. Returns `fallback` (default null) when
 * nothing matches — on the new-version path pass the existing document's
 * `container_type` so a re-upload keeps its routing. Single source for the
 * previously-duplicated detection blocks (WHA-3689).
 */
export function getContainerType(
  filename: string | null | undefined,
  mimeType?: string | null,
  fallback: string | null = null
): string | null {
  const ext = getFileExtension(filename);
  if ((DWG_EXTENSIONS as readonly string[]).includes(ext)) return "dwg";
  if ((IFC_EXTENSIONS as readonly string[]).includes(ext)) return "ifc";
  const isZip =
    (mimeType != null && (ZIP_CONTENT_TYPES as readonly string[]).includes(mimeType)) ||
    (CONTAINER_EXTENSIONS as readonly string[]).includes(ext);
  if (isZip) return "zip";
  return fallback;
}

// Extensions the Franklin smart-processing pipeline knows how to extract.
// Mirrors the branches in franklin-data-pipeline/pipeline/processors/text_extractor.py
// (plus DWG, which has its own pipeline). Used to distinguish "we tried and failed"
// from "we never had a chance" when surfacing processing errors in the UI.
//
// Release order for "csv" (both lists below): it must not reach an environment
// before franklin-data-pipeline#296 (the .csv extraction branch) and chatbot#193
// (query_spreadsheet) are deployed there, and the #297 spreadsheet backfill has
// re-processed CSVs uploaded earlier. Merged early, a CSV the user is now
// allowed to attach extracts nothing, so the chatbot cannot read the file it
// accepted; and a legacy CSV left in ERROR reads as "processing failed"
// (DocumentTableRow, the only consumer of this list outside the upload gates)
// where it used to read as the unsupported type it was.
export const SUPPORTED_FILE_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "docx",
  "doc",
  "xlsx",
  "xls",
  "xlsm",
  "ods",
  "csv",
  "txt",
  "nc1",
  "eml",
  "msg",
  "ifc",
  "zip",
  "asice",
  "bdoc",
  "ddoc",
  "dwg",
] as const;

const SUPPORTED_FILE_EXTENSIONS_SET: ReadonlySet<string> = new Set(SUPPORTED_FILE_EXTENSIONS);

/**
 * Check whether a filename's extension is one Franklin's pipeline can process.
 * Returns false for filenames without an extension.
 */
export function isSupportedFiletype(filename: string | null | undefined): boolean {
  if (!filename) return false;
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot < 0 || dot === lower.length - 1) return false;
  return SUPPORTED_FILE_EXTENSIONS_SET.has(lower.slice(dot + 1));
}

// Strict subset of SUPPORTED_FILE_EXTENSIONS — only formats that
// text_extractor.py actually produces text from. Excludes containers
// (.zip/.asice/.bdoc/.ddoc — yield an empty page) and DWG (goes through
// a separate long-running conversion pipeline, not the chatbot extract
// path). Source of truth: the extension branches of TextExtractor.process in
// franklin-data-pipeline's pipeline/processors/text_extractor.py; spreadsheets
// (xlsx/xls/xlsm/ods/csv) are also answered exactly by the chatbot's
// query_spreadsheet tool, which reads the original file.
export const CHATBOT_SUPPORTED_FILE_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "docx",
  "doc",
  "xlsx",
  "xls",
  "xlsm",
  "ods",
  "csv",
  "txt",
  "nc1",
  "eml",
  "msg",
  "ifc",
] as const;

const CHATBOT_SUPPORTED_FILE_EXTENSIONS_SET: ReadonlySet<string> = new Set(
  CHATBOT_SUPPORTED_FILE_EXTENSIONS
);

/**
 * Check whether a filename's extension is one the chatbot can read.
 * Stricter than `isSupportedFiletype`: excludes container archives and
 * DWG since neither is useful for the chatbot extract path.
 */
export function isChatbotSupportedFiletype(filename: string | null | undefined): boolean {
  if (!filename) return false;
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot < 0 || dot === lower.length - 1) return false;
  return CHATBOT_SUPPORTED_FILE_EXTENSIONS_SET.has(lower.slice(dot + 1));
}

// Extensions with unreliable browser MIME type detection
export const EXTRA_EXTENSIONS = ["ods", "msg", "ifc", "fbx", "nc1"] as const;

/**
 * Map of file extensions to MIME types for files where browser detection is unreliable.
 * Used to set an accurate Content-Type during upload instead of falling back to
 * the overly broad "application/octet-stream".
 */
const EXTENSION_MIME_MAP: Record<string, string> = {
  fbx: "application/x-fbx",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
};

/**
 * Blocked file extensions — dangerous executable/script types that should never
 * be uploaded to a construction document platform.
 */
const BLOCKED_EXTENSIONS = new Set([
  // Windows executables & installers
  "exe",
  "msi",
  "com",
  "scr",
  "pif",
  "cmd",
  "bat",
  // Scripts
  "js",
  "vbs",
  "vbe",
  "wsf",
  "wsh",
  "ps1",
  "psm1",
  // Linux/macOS executables
  "sh",
  "csh",
  "ksh",
  "bin",
  "app",
  // Other dangerous
  "dll",
  "sys",
  "drv",
  "inf",
  "reg",
]);

/**
 * Blocked MIME types — executable content types.
 */
const BLOCKED_MIME_TYPES = new Set([
  "application/x-msdownload",
  "application/x-msdos-program",
  "application/x-executable",
  "application/x-sh",
  "application/x-shellscript",
  "application/x-bat",
  "application/x-msi",
]);

/**
 * OS junk filenames that should be silently ignored during upload.
 * Matches the unzip pipeline's METADATA_IGNORE_PATTERNS.
 */
const IGNORED_FILENAMES = new Set([
  ".ds_store",
  "thumbs.db",
  "desktop.ini",
  "._.trashes",
  ".spotlight-v100",
  ".trashes",
  ".fseventsd",
]);

const IGNORED_PREFIXES = ["._", "~$"];

/**
 * Check whether a file is OS junk that should be silently dropped.
 */
export function isIgnoredFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (IGNORED_FILENAMES.has(name)) return true;
  return IGNORED_PREFIXES.some((p) => name.startsWith(p));
}

/**
 * Check whether a file is blocked from upload.
 * Uses a blacklist approach — all file types are allowed except known
 * dangerous executables and scripts.
 */
export function isBlockedFileType(file: File): boolean {
  // Check MIME type blacklist
  if (file.type && BLOCKED_MIME_TYPES.has(file.type)) {
    return true;
  }

  // Check extension blacklist
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && BLOCKED_EXTENSIONS.has(ext)) {
    return true;
  }

  return false;
}

/**
 * Resolve the Content-Type for a file, using the browser-detected MIME type
 * when available, falling back to extension-based lookup, then "application/octet-stream".
 */
export function resolveContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && ext in EXTENSION_MIME_MAP) return EXTENSION_MIME_MAP[ext];
  return "application/octet-stream";
}
